/**
 * Direct browser launch orchestration for PlaywrightClient.connect().
 *
 * Keeps retry, cancellation, late-success disposal, and bypassed lease cleanup
 * out of the already-large client owner file.
 */
import type { Browser, BrowserContext } from 'playwright';
import type { BrowserMode } from '@chatsdet/types';
import type { BrowserLogger } from '../logger.js';
import { collectBrowserLaunchHostDiagnostics } from './launch-host-diagnostics.js';
import { formatBrowserLaunchError, withBrowserLaunchRetry } from './launch-retry.js';

export interface DirectLaunchBrowserLease {
  leaseId: string;
  release(): Promise<void>;
}

export interface DirectLaunchHandle {
  browser: Browser | null;
  context: BrowserContext;
}

export interface DirectLaunchResult<TLease extends DirectLaunchBrowserLease> {
  browser: Browser | null;
  context: BrowserContext;
  browserLease: TLease | null;
}

async function disposeCancelledLaunch(
  launched: DirectLaunchHandle,
  logger: BrowserLogger
): Promise<void> {
  logger.warn('[DirectPlaywright] Disposing browser from cancelled connect');
  try {
    await launched.context.close();
  } catch (error) {
    logger.warn('[DirectPlaywright] Failed to close cancelled browser context', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
  if (launched.browser) {
    try {
      await launched.browser.close();
    } catch (error) {
      logger.warn('[DirectPlaywright] Failed to close cancelled browser', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}

export async function launchDirectBrowserWithRetry<TLease extends DirectLaunchBrowserLease>(
  options: {
    browserLease: TLease | null;
    browserMode: BrowserMode;
    logger: BrowserLogger;
    signal: AbortSignal;
    launch: () => Promise<DirectLaunchHandle>;
  }
): Promise<DirectLaunchResult<TLease>> {
  let browserLease = options.browserLease;

  if (browserLease) {
    options.logger.info('[DirectPlaywright] Releasing pool lease for direct launch', {
      leaseId: browserLease.leaseId,
      reason: options.browserMode === 'headed' ? 'headed-mode' : 'requires-dedicated-browser',
    });
    await browserLease.release();
    browserLease = null;
  }

  let launched: DirectLaunchHandle;
  try {
    launched = await withBrowserLaunchRetry(() => options.launch(), {
      logger: options.logger,
      label: 'direct-launch',
      signal: options.signal,
      retryTimeouts: false,
      onLateSuccess: (lateLaunch) => disposeCancelledLaunch(lateLaunch, options.logger),
    });
  } catch (error) {
    options.logger.error('[DirectPlaywright] Browser launch exhausted retries', {
      error: formatBrowserLaunchError(error),
      host: collectBrowserLaunchHostDiagnostics(),
    });
    throw error;
  }

  if (options.signal.aborted) {
    await disposeCancelledLaunch(launched, options.logger);
    const reason = options.signal.reason;
    throw reason instanceof Error ? reason : new Error('Browser connect aborted');
  }

  return {
    ...launched,
    browserLease,
  };
}
